/**
 * Fonctions financières d'Excel, avec leur sémantique exacte.
 *
 * Convention de signe d'Excel : ce qu'on verse est négatif, ce qu'on reçoit est
 * positif. `PMT(taux; n; 90000)` renvoie donc un nombre **négatif**. Ces fonctions
 * restent fidèles à cette convention ; c'est la couche métier qui expose des
 * montants positifs.
 *
 * `type` : 0 = versement en fin de période (défaut), 1 = en début de période.
 *
 * Aucune E/S, aucune dépendance : ce fichier ne connaît que des nombres.
 */
/** Valeur future d'une série de versements constants (`FV` d'Excel). */
export function fv(rate, nper, pmt, pv, type = 0) {
    if (rate === 0)
        return -(pv + pmt * nper);
    const growth = (1 + rate) ** nper;
    return -(pv * growth + (pmt * (1 + rate * type) * (growth - 1)) / rate);
}
/** Versement périodique constant (`PMT` d'Excel). */
export function pmt(rate, nper, pv, futureValue = 0, type = 0) {
    if (rate === 0)
        return -(pv + futureValue) / nper;
    const growth = (1 + rate) ** nper;
    return -((pv * growth + futureValue) * rate) / ((1 + rate * type) * (growth - 1));
}
/** Part d'intérêts du versement de la période `per` (`IPMT` d'Excel). */
export function ipmt(rate, per, nper, pv, futureValue = 0, type = 0) {
    // En début de période, le premier versement ne porte aucun intérêt.
    if (type === 1 && per === 1)
        return 0;
    const payment = pmt(rate, nper, pv, futureValue, type);
    const interest = fv(rate, per - 1, payment, pv, type) * rate;
    return type === 1 ? interest / (1 + rate) : interest;
}
/** Part de capital du versement de la période `per` (`PPMT` d'Excel). */
export function ppmt(rate, per, nper, pv, futureValue = 0, type = 0) {
    return pmt(rate, nper, pv, futureValue, type) - ipmt(rate, per, nper, pv, futureValue, type);
}
/**
 * Valeur actuelle nette (`NPV` d'Excel).
 *
 * Attention, c'est un piège classique : Excel actualise **le premier flux d'une
 * période**, il ne le laisse pas à `t = 0`. Un investissement initial se retranche
 * donc à part, hors de la série.
 */
export function npv(rate, values) {
    let total = 0;
    for (let i = 0; i < values.length; i++) {
        total += (values[i] ?? 0) / (1 + rate) ** (i + 1);
    }
    return total;
}
/** VAN d'une série dont le premier flux est à `t = 0` (base de l'IRR). */
function npvFromZero(rate, values) {
    let total = 0;
    for (let i = 0; i < values.length; i++) {
        total += (values[i] ?? 0) / (1 + rate) ** i;
    }
    return total;
}
function npvDerivative(rate, values) {
    let total = 0;
    for (let i = 1; i < values.length; i++) {
        total -= (i * (values[i] ?? 0)) / (1 + rate) ** (i + 1);
    }
    return total;
}
const IRR_TOLERANCE = 1e-12;
const IRR_MAX_NEWTON_ITERATIONS = 100;
const IRR_MAX_BISECTION_ITERATIONS = 400;
/**
 * Taux de rendement interne (`IRR` d'Excel), flux à `t = 0, 1, 2, ...`.
 *
 * Newton-Raphson depuis `guess`, puis repli sur la bissection si Newton diverge,
 * sort du domaine (`taux <= -1`) ou ne converge pas. Renvoie `null` quand aucun
 * taux n'existe — là où Excel affiche `#NUM!`, que le classeur masque en « n.a ».
 */
export function irr(values, guess = 0.1) {
    // Sans flux de chaque signe, la VAN ne s'annule jamais.
    const hasPositive = values.some((v) => v > 0);
    const hasNegative = values.some((v) => v < 0);
    if (!hasPositive || !hasNegative)
        return null;
    let rate = guess;
    for (let i = 0; i < IRR_MAX_NEWTON_ITERATIONS; i++) {
        const value = npvFromZero(rate, values);
        const slope = npvDerivative(rate, values);
        if (!Number.isFinite(value) || !Number.isFinite(slope) || slope === 0)
            break;
        const next = rate - value / slope;
        if (!Number.isFinite(next) || next <= -1)
            break;
        if (Math.abs(next - rate) < IRR_TOLERANCE)
            return next;
        rate = next;
    }
    return irrByBisection(values);
}
function irrByBisection(values) {
    let low = -0.999999;
    let high = 1;
    let valueLow = npvFromZero(low, values);
    let valueHigh = npvFromZero(high, values);
    // Élargit la borne haute tant qu'aucun changement de signe n'est encadré.
    for (let i = 0; i < 60 && valueLow * valueHigh > 0; i++) {
        high *= 2;
        valueHigh = npvFromZero(high, values);
    }
    if (valueLow * valueHigh > 0 || !Number.isFinite(valueHigh))
        return null;
    for (let i = 0; i < IRR_MAX_BISECTION_ITERATIONS; i++) {
        const middle = (low + high) / 2;
        const valueMiddle = npvFromZero(middle, values);
        if (Math.abs(valueMiddle) < IRR_TOLERANCE || (high - low) / 2 < IRR_TOLERANCE) {
            return middle;
        }
        if (valueLow * valueMiddle < 0) {
            high = middle;
            valueHigh = valueMiddle;
        }
        else {
            low = middle;
            valueLow = valueMiddle;
        }
    }
    return (low + high) / 2;
}
//# sourceMappingURL=excel.js.map